import React, { useEffect, useRef, useState } from 'react';
import { placesDisponible, keyRechazada, nuevaSesion, sugerencias as buscarSugerencias, detalle, buscarDireccion } from '../services/placesService';
import {
    PROVINCIAS, CABA, VERIFICADA_GOOGLE, CONFIRMADA_CLIENTE, SIN_GOOGLE,
    desdeGoogle, desdeTexto, componer, faltantes, enLaLocalidad, conLaAltura, linkAlMapa,
} from '../utils/direccion';

// Dirección del negocio del registro: una línea para buscar con Google Places y,
// abajo, el desglose (calle, número, localidad, provincia y un adicional). Al elegir
// una sugerencia, o al salir de la línea habiendo escrito la dirección entera, el
// desglose se completa solo. Lo que se guarda es el desglose, así la dirección
// siempre llega completa aunque Google no esté o no se elija ninguna sugerencia.
//
// Con el desglose completo se confirma en Google Maps que esa calle tenga esa altura
// en esa localidad (direccion.verificacion). Si no la encuentra, ofrece las que sí
// existen en la provincia ("Los Condores 699" está en Villa Carlos Paz, no en Córdoba
// capital) o deja que el cliente confirme que está bien escrita.

// Estilos en pages/Registro.css (registro-*) y pages/Negocio.css (direccion-*).

function CampoDireccion({ linea, onLinea, direccion, onDireccion, onFocus, onBlur }) {
    const [lista, setLista] = useState([]);
    const [abierta, setAbierta] = useState(false);
    const [activa, setActiva] = useState(-1);
    const [mostrarDesglose, setMostrarDesglose] = useState(() => Boolean(direccion.calle));
    const sesion = useRef(nuevaSesion());
    const entrada = useRef(null);
    // La línea que corresponde al desglose actual: si la línea cambia, es una búsqueda nueva.
    const lineaAplicada = useRef(linea);
    // Si Google rechaza la key no se le vuelve a preguntar. Un corte de red, en cambio,
    // se reintenta con la tecla siguiente: el 2026-10-16 un ERR_CONNECTION_CLOSED
    // suelto dejaba sin sugerencias toda la visita.
    const [googleCaido, setGoogleCaido] = useState(false);
    const usarGoogle = placesDisponible() && !googleCaido;
    // Mientras se comprueba en Google Maps: buscando, o no_encontrada con las
    // direcciones con esa altura en otras localidades de la provincia.
    const [comprobacion, setComprobacion] = useState({ estado: 'inicial', opciones: [] });

    useEffect(() => {
        const texto = linea.trim();
        if (!usarGoogle || texto.length < 3 || linea === lineaAplicada.current) {
            setLista([]);
            return undefined;
        }
        const controller = new AbortController();
        const espera = setTimeout(() => {
            buscarSugerencias(texto, sesion.current, controller.signal)
                .then((r) => {
                    setLista(r);
                    setActiva(-1);
                    // Si llegan después de salir de la línea, quedan para cuando vuelva.
                    setAbierta(r.length > 0 && document.activeElement === entrada.current);
                })
                .catch((error) => {
                    if (error.name === 'AbortError') return;
                    console.error('Error con Google Places:', error);
                    setLista([]);
                    if (keyRechazada(error)) setGoogleCaido(true);
                });
        }, 250);
        return () => {
            clearTimeout(espera);
            controller.abort();
        };
    }, [linea, usarGoogle]);

    const aplicar = (nueva, texto) => {
        lineaAplicada.current = texto;
        onLinea(texto);
        onDireccion(nueva);
        setMostrarDesglose(true);
    };
    // Negocio.js arma onLinea en cada render: la comprobación usa siempre el último
    // sin reiniciarse por eso.
    const aplicarRef = useRef(aplicar);
    aplicarRef.current = aplicar;

    // Comprobar en Google Maps la dirección completa que todavía no se comprobó.
    useEffect(() => {
        if (!mostrarDesglose || direccion.verificacion || faltantes(direccion).length > 0) {
            if (!direccion.verificacion) setComprobacion({ estado: 'inicial', opciones: [] });
            return undefined;
        }
        if (!usarGoogle) {
            onDireccion({ ...direccion, verificacion: SIN_GOOGLE });
            return undefined;
        }
        const controller = new AbortController();
        const espera = setTimeout(async () => {
            setComprobacion({ estado: 'buscando', opciones: [] });
            try {
                const lugares = await buscarDireccion(componer({ ...direccion, adicional: '' }), controller.signal);
                const exacta = conLaAltura(lugares, direccion).find((l) => enLaLocalidad(l, direccion));
                if (exacta) {
                    setComprobacion({ estado: 'inicial', opciones: [] });
                    const nueva = { ...exacta, adicional: exacta.adicional || direccion.adicional };
                    aplicarRef.current(nueva, componer(nueva));
                    return;
                }
                // La calle y la altura pueden estar bien y la localidad no: las sugerencias
                // de la provincia en las que Google conoce esa altura.
                const enLaProvincia = await buscarSugerencias(
                    `${direccion.calle} ${direccion.numero}, ${direccion.provincia === CABA ? 'CABA' : direccion.provincia}`,
                    sesion.current,
                    controller.signal
                );
                const altura = direccion.numero.replace(/\D/g, '');
                const opciones = altura
                    ? enLaProvincia.filter((s) => s.conAltura && new RegExp(`\\b${altura}\\b`).test(s.principal)).slice(0, 3)
                    : [];
                setComprobacion({ estado: 'no_encontrada', opciones });
            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error('Error con Google Places:', error);
                if (keyRechazada(error)) setGoogleCaido(true);
                setComprobacion({ estado: 'inicial', opciones: [] });
                onDireccion({ ...direccion, verificacion: SIN_GOOGLE });
            }
        }, 600);
        return () => {
            clearTimeout(espera);
            controller.abort();
        };
    }, [direccion, mostrarDesglose, usarGoogle, onDireccion]);

    const elegir = async (sugerencia) => {
        setAbierta(false);
        setLista([]);
        let nueva;
        try {
            const lugar = await detalle(sugerencia.id, sesion.current);
            nueva = desdeGoogle(lugar, sugerencia.id, linea);
        } catch (error) {
            console.error('Error con Google Places:', error);
            nueva = desdeTexto([sugerencia.principal, sugerencia.secundario].filter(Boolean).join(', '));
        }
        sesion.current = nuevaSesion();
        // El piso o local que ya se hubiera cargado no se pierde al elegir otra dirección.
        nueva = { ...nueva, adicional: nueva.adicional || direccion.adicional };
        aplicar(nueva, componer(nueva));
    };

    const alSalirDeLaLinea = (e) => {
        setAbierta(false);
        onBlur(e);
        // Escribió la dirección entera sin elegir una sugerencia: se desglosa el texto.
        if (linea.trim() && linea !== lineaAplicada.current) {
            const nueva = desdeTexto(linea);
            aplicar({ ...nueva, adicional: nueva.adicional || direccion.adicional }, linea);
        }
    };

    const alTeclear = (e) => {
        if (!abierta || lista.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiva((i) => (i + 1) % lista.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiva((i) => (i <= 0 ? lista.length - 1 : i - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            elegir(lista[activa >= 0 ? activa : 0]);
        } else if (e.key === 'Escape') {
            setAbierta(false);
        }
    };

    const cambiarCampo = (campo) => (e) => {
        const nueva = { ...direccion, [campo]: e.target.value };
        // Editar a mano invalida lo que había dado Google (el código postal y el partido
        // también: con otra altura o localidad pueden ser otros) y hay que volver a comprobarla.
        if (campo !== 'adicional') {
            Object.assign(nueva, { placeId: null, lat: null, lng: null, partido: '', codigoPostal: '', verificacion: null });
        }
        const texto = componer(nueva);
        lineaAplicada.current = texto;
        onLinea(texto);
        onDireccion(nueva);
    };

    const pendientes = mostrarDesglose ? faltantes(direccion) : [];

    let aviso = null;
    if (pendientes.length > 0) {
        // Ya lo dice direccion-pendientes.
    } else if (direccion.verificacion === VERIFICADA_GOOGLE) {
        const link = linkAlMapa(direccion);
        aviso = (
            <div className="direccion-aviso direccion-aviso-ok">
                <span className="direccion-aviso-tilde" aria-hidden="true">✓</span>
                <div>
                    <strong>La encontramos en Google Maps:</strong>{' '}
                    {componer(direccion)}{direccion.codigoPostal ? ` (CP ${direccion.codigoPostal})` : ''}.{' '}
                    {link && <a href={link} target="_blank" rel="noopener noreferrer">Ver en el mapa</a>}
                </div>
            </div>
        );
    } else if (comprobacion.estado === 'buscando') {
        aviso = (
            <div className="direccion-aviso">
                <span className="campo-cuit-spinner" />
                Buscando la dirección en Google Maps…
            </div>
        );
    } else if (comprobacion.estado === 'no_encontrada') {
        const localidad = direccion.provincia === CABA ? 'CABA' : `${direccion.localidad}, ${direccion.provincia}`;
        aviso = (
            <div className="direccion-aviso direccion-aviso-revisar">
                <div>
                    <strong>No encontramos {direccion.calle} {direccion.numero} en {localidad}.</strong>{' '}
                    {comprobacion.opciones.length > 0
                        ? '¿Es alguna de estas?'
                        : 'Revisá la calle, el número y la localidad.'}
                </div>
                {comprobacion.opciones.map((o) => (
                    <button key={o.id} type="button" className="direccion-opcion" onClick={() => elegir(o)}>
                        {o.principal}{o.secundario ? `, ${o.secundario.replace(/, Argentina$/, '')}` : ''}
                    </button>
                ))}
                <label className="direccion-confirmar">
                    <input
                        type="checkbox"
                        checked={direccion.verificacion === CONFIRMADA_CLIENTE}
                        onChange={(e) => onDireccion({ ...direccion, verificacion: e.target.checked ? CONFIRMADA_CLIENTE : null })}
                    />
                    La dirección está bien escrita aunque no figure en Google Maps.
                </label>
            </div>
        );
    }

    return (
        <div className="registro-campo">
            <label htmlFor="negocio-direccion" className="registro-label">Dirección del negocio</label>
            <div style={{ position: 'relative' }}>
                <input
                    ref={entrada}
                    id="negocio-direccion"
                    type="text"
                    name="mainAddress"
                    autoComplete="off"
                    value={linea}
                    onChange={(e) => onLinea(e.target.value)}
                    onFocus={() => {
                        onFocus();
                        if (lista.length > 0) setAbierta(true);
                    }}
                    onBlur={alSalirDeLaLinea}
                    onKeyDown={alTeclear}
                    className="registro-input"
                    placeholder="Ej.: Charlone 1095, San Miguel, Buenos Aires"
                    role="combobox"
                    aria-expanded={abierta}
                    aria-autocomplete="list"
                />
                {abierta && lista.length > 0 && (
                    <div role="listbox" style={{
                        position: 'absolute',
                        zIndex: 1000,
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        boxShadow: '0 12px 28px rgba(10, 31, 68, 0.14)',
                        overflow: 'hidden'
                    }}>
                        {lista.map((s, i) => (
                            <div
                                key={s.id}
                                role="option"
                                aria-selected={i === activa}
                                // mousedown sin foco: si la línea perdiera el foco, se desglosaría el texto a medio escribir.
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => elegir(s)}
                                onMouseEnter={() => setActiva(i)}
                                style={{
                                    padding: '10px 14px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    backgroundColor: i === activa ? '#f1f5f9' : 'white',
                                    borderBottom: i < lista.length - 1 ? '1px solid #f1f5f9' : 'none'
                                }}
                            >
                                <span style={{ fontWeight: 600, color: '#001c4d' }}>{s.principal}</span>
                                {s.secundario && <span style={{ color: '#64748b' }}>{`, ${s.secundario}`}</span>}
                            </div>
                        ))}
                        <div style={{ padding: '4px 14px 6px', textAlign: 'right', fontSize: '11px', color: '#94a3b8' }}>Google Maps</div>
                    </div>
                )}
            </div>

            {mostrarDesglose && (
                <div className="direccion-desglose">
                    {pendientes.length > 0 && (
                        <div className="direccion-pendientes">
                            Completá {pendientes.join(', ').replace(/, ([^,]*)$/, ' y $1')} para que la dirección quede completa.
                        </div>
                    )}
                    {aviso}
                    <div className="direccion-fila direccion-fila-calle">
                        <div className="registro-campo">
                            <label htmlFor="direccion-calle" className="direccion-etiqueta">Calle</label>
                            <input type="text" className="registro-input direccion-input" id="direccion-calle" value={direccion.calle} onChange={cambiarCampo('calle')} required />
                        </div>
                        <div className="registro-campo">
                            <label htmlFor="direccion-numero" className="direccion-etiqueta">Número</label>
                            <input type="text" className="registro-input direccion-input" id="direccion-numero" value={direccion.numero} onChange={cambiarCampo('numero')} required />
                        </div>
                    </div>
                    <div className="direccion-fila">
                        <div className="registro-campo">
                            <label htmlFor="direccion-localidad" className="direccion-etiqueta">{direccion.provincia === CABA ? 'Barrio' : 'Localidad'}</label>
                            <input type="text" className="registro-input direccion-input" id="direccion-localidad" value={direccion.localidad} onChange={cambiarCampo('localidad')} required />
                        </div>
                        <div className="registro-campo">
                            <label htmlFor="direccion-provincia" className="direccion-etiqueta">Provincia</label>
                            <select className="registro-input registro-select direccion-input" id="direccion-provincia" value={direccion.provincia} onChange={cambiarCampo('provincia')} required>
                                <option value="">Elegí la provincia</option>
                                {PROVINCIAS.map((p) => (
                                    <option key={p} value={p}>{p === CABA ? 'Ciudad de Buenos Aires (CABA)' : p}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="registro-campo">
                        <label htmlFor="direccion-adicional" className="direccion-etiqueta">Piso, depto o local <span className="direccion-opcional">(opcional)</span></label>
                        <input type="text" className="registro-input direccion-input" id="direccion-adicional" value={direccion.adicional} onChange={cambiarCampo('adicional')} placeholder="Ej.: Local 1" />
                    </div>
                </div>
            )}
        </div>
    );
}

export default CampoDireccion;
